import type { ReactElement } from 'react';

import styled, { css, useTheme } from 'styled-components';

import { WashingLabelsList } from './WashingLabelsList';
import { AccordionElementColorConfig } from '../sustainabilityTraceability/styles';
import LogoCleverCare from '~/components/shared/core/icons/LogoCleverCare';
import { FesLink } from '~/components/shared/core/link/FesLink';
import { Typography } from '~/components/shared/core/typography/Typography';
import { useLayoutDesignContext } from '~/utilities/context/dynamic/LayoutDesignContext';
import { useAppContext } from '~/utilities/context/static/AppContext';

interface WashingLabelProps {
  title?: string;
  cleverCareLink?: string;
}

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,

  Title: styled(Typography)<{ $hasProductAccordions?: boolean }>`
    ${({ $hasProductAccordions }) =>
      $hasProductAccordions &&
      css`
        text-transform: initial;
        margin-bottom: 0 !important;
      `}
    ${({ theme }) => AccordionElementColorConfig[theme.siteType]};
  `,

  CleverCareIcon: styled(FesLink)<{ $hasProductAccordions?: boolean }>`
    height: 30px;
    width: 100px;
    margin-bottom: ${({ $hasProductAccordions }) => ($hasProductAccordions ? '14px' : '20px')};
  `,
};

export const WashingLabels = ({ title, cleverCareLink }: WashingLabelProps): ReactElement => {
  const { language } = useAppContext();
  const { hasProductAccordions } = useLayoutDesignContext();
  const theme = useTheme();

  return (
    <S.Wrapper>
      <S.Title
        className="washing-labels-title"
        component="h3"
        variant="h2"
        testId="washing-header"
        $hasProductAccordions={hasProductAccordions}
      >
        {title}
      </S.Title>
      <WashingLabelsList />
      {cleverCareLink && (
        <S.CleverCareIcon
          href={`${cleverCareLink}/${language}`}
          data-testid="clevercare-logo"
          aria-label="Clevercare"
          $hasProductAccordions={hasProductAccordions}
        >
          <LogoCleverCare backgroundColor={theme.colors.background.base} />
        </S.CleverCareIcon>
      )}
    </S.Wrapper>
  );
};
